import moment from 'moment'
import { push } from 'react-router-redux'
import type { Dispatch } from 'redux'

import { fetchV2DaysForUser } from '@library/api-deliveries'
import type { Recipe } from '@library/menu-service'

import { removeMissingRecipesFromBasket } from 'actions/removeMissingRecipesFromBasket'
import * as trackingKeys from 'actions/trackingKeys'
import { fetchData } from 'routes/Menu/fetchData'
import { getBasketRecipes } from 'selectors/basket'
import { getDeliveryDetailsInstructions } from 'selectors/deliveryDetails'
import { getOrderType } from 'selectors/order'
import { getUsersOrdersDaySlotLeadTimeIds } from 'selectors/user'
import { basketSum } from 'utils/basket'
import {
  getAvailableDeliveryDays,
  getLandingDay,
  transformDaySlotLeadTimesToMockSlots,
} from 'utils/deliveries'
import logger from 'utils/logger'

import { actionTypes } from './actionTypes'
import {
  basketAddressChange,
  basketDateChange,
  basketPostcodeChange,
  basketSlotChange,
} from './basket'
import status from './status'

type State = any
type GetState = () => State

export { removeMissingRecipesFromBasket }

export const basketDeliveryDaysReceive = (days: any) => ({
  type: actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE,
  days,
})

export function boxSummaryDeliverySlotChosen({ date, slotId }: { date: any; slotId: any }) {
  return async (dispatch: Dispatch<any>) => {
    dispatch(basketDateChange(date))
    dispatch(basketSlotChange(slotId))
    dispatch(
      fetchData({ query: {}, params: {} }, true, undefined, {
        addRecipe: () => false,
      }),
    )
  }
}

export function boxSummaryVisibilityChange(show: boolean, totalPrice?: string) {
  return (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const basketRecipes = state.basket.get('recipes')
    const amountOfRecipesInBasket = basketSum(basketRecipes)

    if (show) {
      const type = trackingKeys.clickViewBasket
      dispatch({
        type,
        trackingData: {
          actionType: type,
          recipes_added: amountOfRecipesInBasket,
        },
      })
    }

    dispatch({
      type: actionTypes.BOXSUMMARY_VISIBILITY_CHANGE,
      show,
      trackingData: {
        actionType: trackingKeys.changeBoxSummaryVisibility,
        show,
        recipes_added: amountOfRecipesInBasket,
        totalPrice,
      },
    })

    if (!show) {
      dispatch(removeMissingRecipesFromBasket())
    }
  }
}

export function boxSummaryDeliveryDaysLoad(
  cutoffDatetimeFrom?: string,
  cutoffDatetimeUntil?: string,
  orderType?: 'signup' | 'transactional' | 'subscription',
) {
  return async (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const { basket, menuCutoffUntil } = state

    dispatch(status.error(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE, false))

    // Bumped regardless of postcode, so this request can tell whether a newer one has started
    // since - a postcode comparison alone can't: two overlapping requests for the *same* postcode
    // (e.g. DeliveryDetails' mount-time load overlapping the address modal's own reload) would
    // both look "current" to each other even though one is stale.
    dispatch({ type: actionTypes.BOXSUMMARY_DELIVERY_DAYS_REQUEST_START })
    const myRequestId = getState().boxSummaryDeliveryDaysRequestId

    const postcode = basket.get('postcode') || null

    /**
     * There could be few sources for `menuCutoffUntil` date:
     *  * the highest priority is to the date provided by the caller of the function
     *    as it has more context and could have different data (e.g. Wizard, delivery steps)
     *  * however if the caller didn't provide one it sources the value from the Redux store;
     *  ** `menuCutoffUntil` is value calculated based on data sourced from `/active-periods` endpoint
     *      which was designed to expose only generic information for currently active periods.
     * There reason why the logic still relies on `menuCutoffUntil` as a last resort: potentially there could be
     * rare condition where the `/active-periods` did not respond but the components with "Delivery slots" proceed
     * to be rendered. When it occurs the customer would see only one week of delivery slots instead of three.
     */
    const cutoffUntil = cutoffDatetimeUntil
      ? moment.utc(cutoffDatetimeUntil).endOf('day').toISOString()
      : menuCutoffUntil

    try {
      const cutoffDatetimeFromFormatted = moment
        .utc(cutoffDatetimeFrom)
        .startOf('day')
        .toISOString()

      const data = await fetchV2DaysForUser({
        filters: {
          cutoff_datetime_from: cutoffDatetimeFromFormatted,
          cutoff_datetime_until: cutoffUntil,
        },
        sort: 'date',
        direction: 'asc',
        postcode,
        order_type: orderType || getOrderType(state),
      })

      const days = transformDaySlotLeadTimesToMockSlots(data)

      const availableDeliveryDays = getAvailableDeliveryDays(
        days,
        cutoffDatetimeFrom,
        getUsersOrdersDaySlotLeadTimeIds(state),
      )

      // Discard this response if a newer request has started since this one began - e.g. an
      // earlier request (the mount-time load in DeliveryDetails, or an overlapping confirm)
      // resolving after a newer one, for the same or a different postcode, has already been
      // validated. Applying it now would silently overwrite the current address's delivery days
      // with stale data.
      if (getState().boxSummaryDeliveryDaysRequestId !== myRequestId) {
        return { success: false as const, discarded: true }
      }

      dispatch(basketDeliveryDaysReceive(availableDeliveryDays))

      return { success: true as const }
    } catch (err: any) {
      if (err.message !== 'do-not-deliver') {
        logger.error(err)
      }

      if (getState().boxSummaryDeliveryDaysRequestId !== myRequestId) {
        return { success: false as const, discarded: true }
      }

      dispatch(status.error(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE, err.message))

      // Returned so callers can identify the outcome of this specific request instead of reading
      // the shared/global error state, which a concurrent request (e.g. the mount-time load in
      // DeliveryDetails running alongside this one for the same postcode) could also be writing
      // to - that shared key can't distinguish "my request failed" from "a different request for
      // the same postcode failed after mine already succeeded".
      return { success: false as const, error: err.message }
    } finally {
      // Dispatched whether this request won, was discarded, or failed - a discarded outcome only
      // tells a caller "don't trust my own result", not "the winning request is done". Consumers
      // that need to know whether boxSummaryDeliveryDays is guaranteed up to date should gate on
      // boxSummaryDeliveryDaysPendingCount reaching zero instead, since whichever request finishes
      // last is always the latest one started.
      dispatch({ type: actionTypes.BOXSUMMARY_DELIVERY_DAYS_REQUEST_END })
    }
  }
}

export const boxSummaryNext = () => (dispatch: Dispatch<any>, getState: GetState) => {
  const state = getState()
  const landing = getLandingDay(state, { useCurrentSlot: true })

  const tempDate = state.temp.get('date', landing.date)
  const tempSlotId = state.temp.get('slotId', landing.slotId)
  const tempOrderId = state.temp.get('orderId', landing.orderId)

  const basketPostcode = state.basket.get('postcode')

  if (basketPostcode && !state.error.get(actionTypes.BOXSUMMARY_DELIVERY_DAYS_RECEIVE)) {
    if (tempOrderId) {
      dispatch(push(`/menu/${tempOrderId}`))
      dispatch(boxSummaryVisibilityChange(false))
    } else {
      dispatch(
        boxSummaryDeliverySlotChosen({
          date: tempDate,
          slotId: tempSlotId,
        }),
      )
      if (getBasketRecipes(state).size === 0) {
        dispatch(boxSummaryVisibilityChange(false))
      }
    }
  } else {
    const tempPostcode = state.temp.get('postcode', '')
    let shippingDefault
    if (state.user.get('shippingAddresses')) {
      shippingDefault = state.user
        .get('shippingAddresses')
        .filter((address: any) => address.get('shippingDefault'))
        .first()
    }

    const chosenAddress = state.basket.get('chosenAddress') || shippingDefault

    if (chosenAddress) {
      dispatch(basketAddressChange(chosenAddress))
      dispatch(basketPostcodeChange(chosenAddress.get('postcode')))
    } else if (tempPostcode && tempPostcode.trim() !== '') {
      const postcode = state.temp.get('postcode')
      dispatch(basketPostcodeChange(postcode))
    }
  }
}

export const trackingUnavailableRecipeList = (unavailableRecipeList: any) => ({
  type: actionTypes.TRACKING_UNAVAILABLE_RECIPE_LIST,
  trackingData: {
    actionType: trackingKeys.unavailableRecipeList,
    unavailableRecipeList: unavailableRecipeList.map((recipe: Recipe) => recipe.id),
  },
})

export const trackDeliveryInstructionSelection =
  () => (dispatch: Dispatch<any>, getState: GetState) => {
    const deliveryInstruction = getDeliveryDetailsInstructions(getState())

    dispatch({
      type: actionTypes.TRACKING,
      trackingData: {
        actionType: trackingKeys.deliveryInstructionDetailsClick,
        selection: deliveryInstruction,
      },
    })
  }

export const trackDeliveryOptionsShown = () => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: trackingKeys.deliveryOptionsPostCodeShown,
    shown: true,
  },
})

export const trackRecipeChangeWarning = (tempDate: string) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: trackingKeys.recipeChangeWarning,
    tempDate,
  },
})

export const trackBoxSizeSelectionChange = (boxSize: number, view: string) => ({
  type: actionTypes.TRACKING,
  trackingData: {
    actionType: trackingKeys.radioBoxSizeSelected,
    view,
    boxSize,
  },
})
